import { auth } from '../../firebase/config';

/**
 * Thin authenticated fetch wrapper for the WhatsApp backend.
 *
 * All sends go through the server: the Graph API access token is a server-only
 * secret, and the 24h-window rule must be enforced somewhere the client cannot
 * bypass. The client never calls graph.facebook.com directly.
 */

const API_BASE =
  process.env.REACT_APP_WHATSAPP_API_BASE || '/api/whatsapp';

export class WhatsAppApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'WhatsAppApiError';
  }
}

const getAuthToken = async (): Promise<string> => {
  const user = auth?.currentUser;
  if (!user) {
    throw new WhatsAppApiError('Not authenticated', 401);
  }
  return user.getIdToken();
};

const request = async <T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> => {
  const token = await getAuthToken();
  const { method = 'GET', body, signal } = options;

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (!response.ok) {
    let message = `Request failed with ${response.status}`;
    let code: string | undefined;
    try {
      const payload = await response.json();
      message = payload.error || payload.message || message;
      code = payload.code;
    } catch {
      // Non-JSON error body — keep the status-based message.
    }
    throw new WhatsAppApiError(message, response.status, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
};

export { API_BASE };
